import { captureAttribution, toSharedAttribution } from './attribution';
import { readCid } from './cid';
import { CTA_SELECTOR, createCtaTracker } from './cta';
import { pushDataLayer } from './datalayer';
import { FORM_SELECTOR, createFormTracker } from './form';
import { getOrCreateSessionId, getOrCreateVisitorId } from './ids';
import { createLogger } from './logger';
import { createTransport } from './transport';
import type { BootConfig, FormSubmitMode, Logger, StoredAttribution } from './types';
import { VIDEO_SELECTOR, createVideoTracker } from './video';

function normalizeSubmitMode(value: string | undefined): FormSubmitMode {
  return value === 'blocking' || value === 'api_only' || value === 'non_blocking'
    ? value
    : 'non_blocking';
}

function findScript(): HTMLScriptElement | null {
  const current = document.currentScript;
  if (current instanceof HTMLScriptElement) return current;
  return document.querySelector<HTMLScriptElement>('script[data-tenant-id]');
}

function readConfig(script: HTMLScriptElement | null): BootConfig | null {
  if (!script) {
    console.warn('[VLT] boot aborted: script tag not found');
    return null;
  }

  const { tenantId, siteId, lpId, apiBase } = script.dataset;
  if (!tenantId || !siteId || !lpId || !apiBase) {
    console.warn('[VLT] boot aborted: missing required data attributes');
    return null;
  }

  return {
    tenantId,
    siteId,
    lpId,
    apiBase,
    debug: script.dataset.debug === 'true',
    formSubmitMode: normalizeSubmitMode(script.dataset.formSubmitMode),
  };
}

function sendHeartbeat(config: BootConfig, logger: Logger): void {
  const url = `${config.apiBase}/api/v1/heartbeat`;
  const body = JSON.stringify({
    tenantId: config.tenantId,
    siteId: config.siteId,
    lpId: config.lpId,
  });

  try {
    if (typeof navigator.sendBeacon === 'function') {
      const ok = navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
      if (ok) return;
    }
  } catch {
    // Fall through to fetch fallback.
  }

  fetch(url, {
    method: 'POST',
    body,
    headers: { 'Content-Type': 'application/json' },
    keepalive: true,
  }).catch(() => logger.debug('heartbeat.failed'));
}

function runBoot(config: BootConfig): void {
  if (window.__VLT_BOOTED__) return;
  window.__VLT_BOOTED__ = true;

  const logger = createLogger(config.debug);
  const visitorId = getOrCreateVisitorId();
  const sessionId = getOrCreateSessionId();
  let attribution: StoredAttribution = captureAttribution();
  const transport = createTransport({ apiBase: config.apiBase, logger });

  const getCommonPayload = () => {
    attribution = captureAttribution();
    return {
      tenant_id: config.tenantId,
      site_id: config.siteId,
      landing_page_id: config.lpId,
      visitor_id: visitorId,
      session_id: sessionId,
      cid: readCid(),
      page_location: window.location.href,
      referrer: document.referrer || null,
      attribution: toSharedAttribution(attribution),
    };
  };

  const videoTracker = createVideoTracker({
    getCommonPayload,
    transport,
    logger,
    datalayer: { pushDataLayer },
  });
  const formTracker = createFormTracker({
    config,
    getCommonPayload,
    getPrimaryVideoSummary: videoTracker.pickPrimaryVideoSummary,
    transport,
    logger,
    datalayer: { pushDataLayer },
  });
  const ctaTracker = createCtaTracker({
    videoTracker,
    transport,
    getCommonPayload,
    targets: [],
    logger,
  });

  document
    .querySelectorAll<HTMLVideoElement | HTMLIFrameElement>(VIDEO_SELECTOR)
    .forEach(videoTracker.attach);
  document.querySelectorAll<HTMLFormElement>(FORM_SELECTOR).forEach(formTracker.attach);
  document.querySelectorAll<HTMLElement>(CTA_SELECTOR).forEach(ctaTracker.attach);

  videoTracker.observe();
  formTracker.observe();
  ctaTracker.observe();

  transport
    .fetchCtaTargets({
      tenantId: config.tenantId,
      siteId: config.siteId,
      landingPageId: config.lpId,
    })
    .then((targets) => ctaTracker.setManagedTargets(targets))
    .catch((error) => logger.warn('cta_targets.fetch_failed', error));

  window.VLT = {
    visitorId: () => visitorId,
    sessionId: () => sessionId,
    attribution: () => attribution,
    videos: () => videoTracker.states(),
    flushQueue: () => undefined,
    version: typeof VLT_VERSION === 'string' ? VLT_VERSION : '1.0.0',
  };

  sendHeartbeat(config, logger);
  logger.debug('boot.complete', { visitorId, sessionId, attribution });
}

export function boot(): void {
  const config = readConfig(findScript());
  if (!config) return;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => runBoot(config), { once: true });
    return;
  }

  runBoot(config);
}
